import { useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import classNames from 'classnames/bind'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'

// Libs
import { useLiveList } from '@/components/shared/LiveList/hooks/useLiveList'
import useMedia from '@/hooks/useMedia'

// Components
import Image from '@/components/common/Image'
import Navigation from '@/components/shared/Navigation'
import HotSectionTitle from '../utils/HotSectionTitle'
import LiveShoppingCard from './LiveShoppingCard'

// Constants
import { ROOM_LIVE_TYPE, ROOM_MODE } from '@/constants/liveRoom'
import { ROUTE_PATH } from '@/constants/routePath'

// Types
import type { LiveListType } from '@/components/shared/LiveList/hooks/useLiveList'

// Styles
import styles from './style.module.scss'

const cx = classNames.bind(styles)

/** 跟原本「現在直播」「即將直播」一樣，最多載到 36 間 */
const MAX_ROOMS = 36

/**
 * 首頁「直播購物」（Figma 首頁V2 Frame 101530）：取代原本的「現在直播」與「即將直播」兩區。
 *
 *   - 直播中與預告的一般直播間放在同一排，直播中排前面
 *   - 競標直播間不放這裡（上面的「限時競標」已經有）
 *   - 每排 5 / 4 / 3 / 2 張（≥1200 / ≥1024 / ≥710 / <710）
 */
const LiveShopping = () => {
  const { t } = useTranslation()
  const { isMobile } = useMedia()

  const {
    data: liveRoomListRaw,
    fetchNextPage,
    hasNextPage,
  } = useLiveList({
    params: {
      pageSize: 10,
      liveTypes: [ROOM_LIVE_TYPE.CURRENT, ROOM_LIVE_TYPE.FUTURE],
      roomMode: ROOM_MODE.DEFAULT,
    },
  })

  // 後端已依 room_mode 篩選；前端再濾一次，避免後端還沒上版時把競標房混進來
  const liveRooms = (liveRoomListRaw?.pages.flatMap((page) => page?.roomList ?? []) ?? [])
    .filter((room): room is LiveListType => !!room && room.roomMode !== ROOM_MODE.AUCTION)
    .sort((a, b) => Number(b.liveType === ROOM_LIVE_TYPE.CURRENT) - Number(a.liveType === ROOM_LIVE_TYPE.CURRENT))
    .slice(0, MAX_ROOMS)

  const swiperRef = useRef<SwiperType>()
  const [edge, setEdge] = useState({ isBeginning: true, isEnd: true })

  const updateEdge = (swiper: SwiperType) => setEdge({ isBeginning: swiper.isBeginning, isEnd: swiper.isEnd })

  const handleReachEnd = (swiper: SwiperType) => {
    updateEdge(swiper)
    if (hasNextPage && liveRooms.length < MAX_ROOMS) fetchNextPage()
  }

  if (!liveRooms.length) return null

  return (
    <section className={cx('live-shopping')}>
      <HotSectionTitle className={cx('live-shopping__header')}>
        <span className={cx('live-shopping__title-icon')}>
          <span className={cx('live-shopping__title-icon-shape')}>
            <Image src='/images/svg/home-live/live-shopping.svg' fill alt='' />
          </span>
        </span>
        <h2 className={cx('live-shopping__title')}>{t('Home_Live_Shopping_Title')}</h2>
        <Link href={ROUTE_PATH.LIVE} className={cx('live-shopping__more-button')}>
          {t('Common_More_Live_Streaming_Rooms')}
        </Link>
      </HotSectionTitle>

      <div className={cx('live-shopping__slider')}>
        <Swiper
          slidesPerView={2}
          spaceBetween={10}
          breakpoints={{
            710: { slidesPerView: 3, spaceBetween: 20 },
            1024: { slidesPerView: 4, spaceBetween: 20 },
            1200: { slidesPerView: 5, spaceBetween: 20 },
          }}
          onSwiper={(swiper: SwiperType) => {
            swiperRef.current = swiper
            updateEdge(swiper)
          }}
          onSlideChange={updateEdge}
          onResize={updateEdge}
          onReachEnd={handleReachEnd}
          onReachBeginning={updateEdge}
        >
          {liveRooms.map((room) => (
            <SwiperSlide key={room.roomId} className={cx('live-shopping__slide')}>
              <LiveShoppingCard {...room} />
            </SwiperSlide>
          ))}
        </Swiper>

        {!isMobile && !edge.isBeginning && (
          <Navigation
            className={cx('live-shopping__arrow', 'live-shopping__arrow--left')}
            direction='left'
            onClick={() => swiperRef.current?.slidePrev()}
          />
        )}
        {!isMobile && !edge.isEnd && (
          <Navigation
            className={cx('live-shopping__arrow', 'live-shopping__arrow--right')}
            direction='right'
            onClick={() => swiperRef.current?.slideNext()}
          />
        )}
      </div>
    </section>
  )
}

export default LiveShopping
